import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Popover,
  IconButton,
  Typography,
} from "@mui/material";
import { Add as AddIcon, EmojiEmotions } from "@mui/icons-material";
import { BASE_URL } from "../../../../services/configUrls";
import { AppleStyleSpinner } from "./AppleStyleSpinner";

// Comprehensive symbol options organized by category
const SYMBOL_CATEGORIES = {
  Status: ["✓", "✔", "✅", "☑", "✗", "✘", "❌", "❎"],
  Stars: ["★", "⭐", "✨", "💫", "🌟"],
  Arrows: ["→", "➤", "▶", "►", "⇒", "➔", "➜", "⬆", "⬇", "⬅", "➡"],
  Shapes: ["●", "○", "◆", "◇", "■", "□", "▪", "▫", "♦", "⬛", "⬜"],
  Basic: ["*", "#", "@", "!", "~", "^", "+", "-", "=", "/"],
  Alert: ["⚠", "⚡", "❗", "❕", "❓", "❔"],
  Premium: ["👑", "💎", "🏆", "🎖", "🥇", "🥈", "🥉"],
  Colored: ["🔴", "🟢", "🟡", "🔵", "🟣", "🟠", "⚫", "⚪", "🟤"],
  Math: ["➕", "➖", "✖", "➗", "♾"],
  Others: ["📌", "📍", "🔖", "🎯", "⚙", "🔒", "🔓", "🔑", "💼", "📊"],
};

const CreateTextStatusModalForMem = ({
  open,
  onClose,
  newStatusText,
  setNewStatusText,
  newStatusSymbol,
  setNewStatusSymbol,
  onCreateTextStatus,
  onRefreshStatusOptions,
  onShowSnackbar,
  onResetTempValue,
}) => {
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("Status");

  const handleOpenPicker = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget.closest(".MuiTextField-root"));
  };

  const handleClosePicker = () => {
    setAnchorEl(null);
  };

  const handleSymbolSelect = (symbol) => {
    setNewStatusSymbol(symbol);
    handleClosePicker();
  };

  const pickerOpen = Boolean(anchorEl);

  const handleClose = () => {
    setNewStatusText("");
    setNewStatusSymbol("");
    setAnchorEl(null);
    if (onResetTempValue) {
      onResetTempValue();
    }
    onClose();
  };

  const handleCreate = async () => {
    if (!newStatusText.trim()) return;

    setLoading(true);

    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(`${BASE_URL}/accounts/status-text-option`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          status_text: newStatusText.trim(),
          symbol: newStatusSymbol.trim(),
        }),
      });

      const result = await response.json();

      if (response.ok) {
        onShowSnackbar({
          open: true,
          message: result.message || "Status created successfully",
          severity: "success",
        });

        await onRefreshStatusOptions();

        if (onResetTempValue) {
          onResetTempValue(newStatusText.trim());
        }

        if (onCreateTextStatus) {
          onCreateTextStatus();
        }

        setNewStatusText("");
        setNewStatusSymbol("");
        onClose();
      } else {
        onShowSnackbar({
          open: true,
          message: result.message || "Failed to create status",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error creating status:", error);
      onShowSnackbar({
        open: true,
        message: "Error creating status. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: "15px", fontWeight: 600, pb: 1 }}>
          Create New Text Status
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {/* Status Text Field with Outside Label */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: "#666",
                mb: 0.5,
              }}
            >
              Status Text
            </Typography>
            <TextField
              fullWidth
              value={newStatusText}
              onChange={(e) => setNewStatusText(e.target.value)}
              placeholder="e.g., Eligible, Premium"
              size="small"
              variant="outlined"
              disabled={loading}
            />
          </Box>

          {/* Symbol Field with Outside Label */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: "#666",
                mb: 0.5,
              }}
            >
              Symbol
            </Typography>
            <TextField
              fullWidth
              value={newStatusSymbol}
              onChange={(e) => setNewStatusSymbol(e.target.value)}
              placeholder="Choose or type custom symbol"
              size="small"
              variant="outlined"
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={handleOpenPicker}
                    disabled={loading}
                    size="small"
                    sx={{ mr: -0.5 }}
                  >
                    <EmojiEmotions fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={!newStatusText.trim() || loading}
            startIcon={loading ? <AppleStyleSpinner /> : <AddIcon />}
            sx={{ textTransform: "none" }}
          >
            {loading ? "Creating..." : "Create Status"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Symbol Picker Popover - WhatsApp Style */}
      <Popover
        open={pickerOpen}
        anchorEl={anchorEl}
        onClose={handleClosePicker}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              width: 450,
              height: 350,
              overflow: "hidden",
              borderRadius: 2,
              mt: 0.5,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              border: "1px solid #e0e0e0",
            },
          },
        }}
      >
        <Box sx={{ display: "flex", height: "100%" }}>
          {/* Categories Sidebar on Left */}
          <Box
            sx={{
              width: 110,
              borderRight: "1px solid #e0e0e0",
              bgcolor: "#fafafa",
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
              overflowX: "hidden",
              "&::-webkit-scrollbar": {
                width: 6,
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#d0d0d0",
                borderRadius: 3,
                "&:hover": {
                  background: "#b0b0b0",
                },
              },
            }}
          >
            {Object.keys(SYMBOL_CATEGORIES).map((category) => (
              <Box
                key={category}
                onClick={() => setSelectedCategory(category)}
                sx={{
                  px: 2,
                  py: 1.8,
                  fontSize: "12px",
                  fontWeight: selectedCategory === category ? 600 : 400,
                  color: selectedCategory === category ? "#1976d2" : "#555",
                  bgcolor:
                    selectedCategory === category ? "#e3f2fd" : "transparent",
                  borderLeft:
                    selectedCategory === category
                      ? "3px solid #1976d2"
                      : "3px solid transparent",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  userSelect: "none",
                  "&:hover": {
                    bgcolor:
                      selectedCategory === category ? "#e3f2fd" : "#f0f0f0",
                  },
                }}
              >
                {category}
              </Box>
            ))}
          </Box>

          {/* Symbol Grid on Right */}
          <Box
            sx={{
              flex: 1,
              bgcolor: "#fff",
              overflowY: "auto",
              overflowX: "hidden",
              "&::-webkit-scrollbar": {
                width: 7,
              },
              "&::-webkit-scrollbar-track": {
                background: "#f9f9f9",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#d0d0d0",
                borderRadius: 4,
                "&:hover": {
                  background: "#b0b0b0",
                },
              },
            }}
          >
            <Box
              sx={{
                p: 2.5,
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 1.5,
                justifyItems: "center",
              }}
            >
              {SYMBOL_CATEGORIES[selectedCategory].map((symbol, index) => (
                <Box
                  key={`${symbol}-${index}`}
                  onClick={() => handleSymbolSelect(symbol)}
                  sx={{
                    width: 40,
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "22px",
                    borderRadius: 1.5,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    userSelect: "none",
                    "&:hover": {
                      bgcolor: "#f0f0f0",
                      transform: "scale(1.15)",
                    },
                    "&:active": {
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  {symbol}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default CreateTextStatusModalForMem;
